import type { GenericError, RequestStatus } from '@skynet/data-ws-core';
import type { AccountStatus } from '@skynet/types-account-status';

// ---------------------------------------------------------------------------
// Backend response types (from the WS frames of `Listen { section: "api-keys" }`)
//
// Accounts come level by level: a plain Listen answers with `MainAccounts`, a
// Listen naming a main account (`mainAccountID`) with that account's
// `SubAccounts`. An account carries its own API keys and balances.
// ---------------------------------------------------------------------------

/**
 * A list of an envelope. The backend (Go) encodes an empty list as `null`, and
 * may leave a list it has nothing for out altogether — both mean "none".
 */
export type JsonList<T> = T[] | null | undefined;

export interface ApiKeyEntry {
  /** The key's own id, which `UpdateAPIKey` addresses a key by. */
  id: number;
  name: string;
  type: number;
  status: AccountStatus;
  /** The key as the backend sends it, e.g. `fAYp.....piZr`: what copying puts on the clipboard. */
  key: string;
}

export interface JsonSubAccountAsset {
  currency: string;
  amount: number;
  /** Price of `currency`, denominated in the quote currency of its market. */
  price: number;
  /** USD price of that quote currency. */
  quotePrice: number;
}

/** A main account or a sub-account, as either frame lists it. */
export interface JsonAccount {
  id: number;
  exchangeID: number;
  name: string;
  uid: string | null;
  note: string | null;
  apiKeys?: JsonList<ApiKeyEntry>;
  assets?: JsonList<JsonSubAccountAsset>;
}

export interface MainAccountsResponse {
  command: 'MainAccounts';
  list?: JsonList<JsonAccount>;
}

export interface SubAccountsResponse {
  command: 'SubAccounts';
  /** The main account the list belongs to, as the Listen named it. */
  mainAccountID: number;
  /** Empty when the main account has no sub-accounts, or is unknown to the backend. */
  list?: JsonList<JsonAccount>;
}

/** One token balance, normalised for rendering. */
export interface SubAccountAsset {
  token: string;
  volume: number;
  /** USD price of one token: `price × quotePrice`. */
  priceUSD: number;
  /** `volume × priceUSD`. */
  volumeUSD: number;
}

/** An account of either level, normalised: its lists always present. */
export interface MainAccount {
  id: number;
  exchangeID: number;
  name: string;
  uid: string | null;
  note: string | null;
  apiKeys: ApiKeyEntry[];
  /** The account's own balance, as reported by the backend. */
  assets: SubAccountAsset[];
}

export interface SubAccount extends MainAccount {
  /** The main account it belongs to (the `SubAccounts` frame's `mainAccountID`). */
  mainAccountID: number;
}

// ---------------------------------------------------------------------------
// Backend response types (from WS "SupportedOperations" command on section: "api-keys")
//
// Which operations each exchange supports, per kind of item. Sent on every
// Listen of the section, with or without `mainAccountID`.
// ---------------------------------------------------------------------------

/** The kinds of item the `SupportedOperations` frame has flags for. */
export type SupportedOperationItem =
  | 'mainAccounts'
  | 'mainAccountApiKeys'
  | 'subAccounts'
  | 'subAccountApiKeys';

export interface OperationFlags {
  create: boolean;
  delete: boolean;
  rename: boolean;
}

/** The flags of one exchange: every item kind, every operation. */
export type ExchangeSupportedOperations = Record<SupportedOperationItem, OperationFlags>;

export interface SupportedOperationsResponse {
  command: 'SupportedOperations';
  /**
   * Keyed by exchange id. Trusted only as far as its shape: the slice
   * normalises it, a missing or non-boolean flag reading as `false`.
   */
  perExchange: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// WS commands handled by this slice
// ---------------------------------------------------------------------------

export enum SubAccountCommands {
  MainAccounts = 'MainAccounts',
  SubAccounts = 'SubAccounts',
  SupportedOperations = 'SupportedOperations',
  DeleteAPIKey = 'DeleteAPIKey',
}

export interface DeleteApiKeyPayload {
  exchange: number;
  label: string;
}

// ---------------------------------------------------------------------------
// List row types
//
// One tree per main account: its keys and sub-accounts, and under a
// sub-account its keys. Main accounts are listed in exchange order, ungrouped.
// A main account's sub-accounts are there only once they have been loaded.
// ---------------------------------------------------------------------------

export type EntityLevel = 'account' | 'subAccount' | 'apiKey';

interface BaseEntityRow {
  key: string;
  level: EntityLevel;
  label: string;
  /** Exchange the entity belongs to. */
  exchangeID: number;
}

/** Expandable: its own keys, then its sub-accounts. */
export interface AccountRow extends BaseEntityRow {
  level: 'account';
  data: MainAccount;
  /** The account's own balance, as reported by the backend. */
  assets: SubAccountAsset[];
  /** Labels of all the account's own API keys, whether or not the search shows them. */
  apiKeyLabels: string[];
  /**
   * Progress of loading the account's sub-accounts: `null` until they are
   * asked for, then the status of the last `SubAccounts` request.
   */
  subAccountsStatus: RequestStatus;
  /** Whether a `SubAccounts` frame has arrived for it: its sub-account rows are complete. */
  subAccountsLoaded: boolean;
  children: (ApiKeyRow | SubAccountRow)[];
}

/** Expandable: its keys. */
export interface SubAccountRow extends BaseEntityRow {
  level: 'subAccount';
  data: SubAccount;
  /** The sub-account's own balance, as reported by the backend. */
  assets: SubAccountAsset[];
  children: ApiKeyRow[];
}

export interface ApiKeyRow extends BaseEntityRow {
  level: 'apiKey';
  /**
   * Carried on the row (via `exchangeID`) because a key names no exchange,
   * while `DeleteAPIKey` addresses a key by `{ exchange, label }`.
   */
  data: ApiKeyEntry;
  /** Whether the key hangs off a main account or a sub-account. */
  ownerLevel: 'account' | 'subAccount';
  children?: undefined;
}

export type EntityRow = AccountRow | SubAccountRow | ApiKeyRow;

export interface SubAccountsFilters {
  search: string;
  /** Exchange ids to show; empty = all. */
  exchanges: number[];
}

// ---------------------------------------------------------------------------
// Slice state
// ---------------------------------------------------------------------------

export interface SubAccountsState {
  requestStatus: RequestStatus;
  error: GenericError | null;

  mainAccounts: MainAccount[];
  /**
   * Sub-accounts by main account id — only of the main accounts a
   * `SubAccounts` frame has arrived for. Missing means "not loaded", not "none".
   */
  subAccounts: Record<string, SubAccount[]>;
  /**
   * Progress of the `SubAccounts` request of each main account, by its id.
   * Missing means it has not been asked for since the section was (re)opened.
   */
  subAccountsStatus: Record<string, RequestStatus>;

  /**
   * The `SupportedOperations` frame, by exchange id. An exchange missing from
   * it — or every exchange, until the frame arrives — supports nothing.
   */
  supportedOperations: Record<string, ExchangeSupportedOperations>;

  filters: SubAccountsFilters;

  /** Progress of the last `DeleteAPIKey`; the other mutations are tracked by the apiKeys slice. */
  resRemove: RequestStatus;
}

// ---------------------------------------------------------------------------
// ok response shape (for mutation confirmations)
// ---------------------------------------------------------------------------

export interface SubAccountOkResponse {
  command: 'ok';
  okCommand: SubAccountCommands;
  result: string;
  id: string;
}
